import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Megaphone, Tag, MessageSquare, BarChart3, Truck, TrendingUp, Mail, Gauge, Package, Users, Inbox, Settings, ChevronDown, Search, Star } from "lucide-react";

const TABS = [
  ["overview", "Overview", Gauge],
  ["orders", "Orders", Package],
  ["marketing", "Marketing", Megaphone],
  ["referrals", "Referrals", Users],
  ["ambassadors", "Ambassadors", Star],
  ["inbox", "Inbox", Inbox],
  ["config", "Config", Settings],
];
import SocialStudio from "@/components/SocialStudio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const campaignBtnCls = (isArmed) =>
  `mt-5 h-12 w-full font-anton uppercase tracking-wider text-sm transition-[background-color,color,transform] duration-200 active:scale-[0.98] disabled:opacity-60 ${
    isArmed ? "bg-white text-black" : "bg-volt text-black hover:bg-volt-dark"
  }`;

const Admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.authError || "");
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const [blasts, setBlasts] = useState([]);
  const [sendingCampaign, setSendingCampaign] = useState(null);
  const [armed, setArmed] = useState(null);
  const [campaignResult, setCampaignResult] = useState("");
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({ total: 0, revenue: 0 });
  const [trackingInputs, setTrackingInputs] = useState({});
  const [editingShip, setEditingShip] = useState({});
  const [shipping, setShipping] = useState(null);
  const [trackingConfigured, setTrackingConfigured] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [discCode, setDiscCode] = useState("");
  const [discKind, setDiscKind] = useState("percent");
  const [discValue, setDiscValue] = useState("");
  const [discSaving, setDiscSaving] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [refEmail, setRefEmail] = useState("");
  const [refSaving, setRefSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [trafficWindow, setTrafficWindow] = useState("24h");
  const [walkupCount, setWalkupCount] = useState("10");
  const [walkupLabel, setWalkupLabel] = useState("");
  const [walkupSaving, setWalkupSaving] = useState(false);
  const [walkupBatches, setWalkupBatches] = useState([]);
  const [tab, setTab] = useState(() => {
    const h = window.location.hash.replace("#", "");
    return TABS.some(([id]) => id === h) ? h : "overview";
  });
  const [refSearch, setRefSearch] = useState("");
  const [showWalkupCodes, setShowWalkupCodes] = useState(false);
  const [openBatches, setOpenBatches] = useState(new Set());
  const [ambName, setAmbName] = useState("");
  const [ambEmail, setAmbEmail] = useState("");
  const [ambSending, setAmbSending] = useState(false);
  const [ambLink, setAmbLink] = useState("");
  const [ambassadors, setAmbassadors] = useState([]);
  const [ambInvites, setAmbInvites] = useState([]);
  const [ambApps, setAmbApps] = useState([]);
  const [ambContract, setAmbContract] = useState("");
  const [ambContractSaving, setAmbContractSaving] = useState(false);
  const [armedPayout, setArmedPayout] = useState(null);
  const [starterTracking, setStarterTracking] = useState({});

  const goTab = (id) => {
    setTab(id);
    window.history.replaceState(null, "", `#${id}`);
    window.scrollTo(0, 0);
  };
  const [popupLeads, setPopupLeads] = useState(null);
  const [nudgingPopup, setNudgingPopup] = useState(false);
  const [messages, setMessages] = useState([]);
  const [syncingShip, setSyncingShip] = useState(false);
  const [shipCfg, setShipCfg] = useState(null);
  const [shipFee, setShipFee] = useState("7.99");
  const [shipThreshold, setShipThreshold] = useState("50");
  const [shipFreeAll, setShipFreeAll] = useState(false);
  const [savingShipCfg, setSavingShipCfg] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [funnelWindow, setFunnelWindow] = useState("30d");

  const onSyncShipping = async () => {
    setSyncingShip(true);
    try {
      const { data } = await axios.post(`${API}/orders/backfill-shipping`, {}, { withCredentials: true });
      toast.success(`Synced ${data.updated} address${data.updated === 1 ? "" : "es"} (${data.checked} orders checked).`);
      loadOrders();
    } catch {
      toast.error("Could not sync addresses.");
    } finally {
      setSyncingShip(false);
    }
  };

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAuth(data);
      } catch {
        setAuth(false);
      } finally {
        setChecking(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFounders = async () => {
    setDataLoading(true);
    try {
      const { data } = await axios.get(`${API}/founders`, { withCredentials: true });
      setEntries(data.entries);
      setTotal(data.total);
    } catch {
      toast.error("Could not load founders.");
    } finally {
      setDataLoading(false);
    }
  };

  const loadBlasts = async () => {
    try {
      const { data } = await axios.get(`${API}/email/blasts`, { withCredentials: true });
      setBlasts(data.blasts);
    } catch {
      /* history is optional */
    }
  };

  const loadAmbassadors = async () => {
    try {
      const [{ data: ambs }, { data: invs }, { data: contract }, { data: apps }] = await Promise.all([
        axios.get(`${API}/ambassadors/admin`, { withCredentials: true }),
        axios.get(`${API}/ambassadors/invites`, { withCredentials: true }),
        axios.get(`${API}/ambassadors/contract`),
        axios.get(`${API}/ambassadors/applications`, { withCredentials: true }),
      ]);
      setAmbassadors(ambs);
      setAmbInvites(invs);
      setAmbContract(contract.text);
      setAmbApps(apps);
    } catch {
      toast.error("Could not load ambassadors.");
    }
  };

  const onAppStatus = async (id, status) => {
    try {
      await axios.post(`${API}/ambassadors/applications/${id}/status`, { status }, { withCredentials: true });
      loadAmbassadors();
    } catch {
      toast.error("Couldn't update application.");
    }
  };

  const onAppInvite = (app) => {
    setAmbName(app.name);
    setAmbEmail(app.email);
    window.scrollTo(0, 0);
    toast.success(`${app.name}'s details loaded into the invite form`);
  };

  const onInviteAmbassador = async (sendEmail) => {
    if (!ambName.trim() || !ambEmail.trim()) {
      toast.error("Enter their name and email.");
      return;
    }
    setAmbSending(true);
    setAmbLink("");
    try {
      const { data } = await axios.post(
        `${API}/ambassadors/invite`,
        { name: ambName.trim(), email: ambEmail.trim(), send_email: sendEmail },
        { withCredentials: true }
      );
      setAmbLink(data.link);
      setAmbName("");
      setAmbEmail("");
      toast.success(sendEmail && data.emailed ? "Invite emailed" : "Invite link created");
      loadAmbassadors();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invite failed.");
    } finally {
      setAmbSending(false);
    }
  };

  const onStarterToggle = async (id) => {
    try {
      await axios.post(
        `${API}/ambassadors/${id}/starter`,
        { tracking_number: starterTracking[id] || "" },
        { withCredentials: true }
      );
      setStarterTracking((m) => ({ ...m, [id]: "" }));
      loadAmbassadors();
    } catch {
      toast.error("Couldn't update starter pack.");
    }
  };

  const onAmbStatus = async (id, status) => {
    try {
      await axios.post(`${API}/ambassadors/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Ambassador ${status}`);
      loadAmbassadors();
    } catch {
      toast.error("Couldn't update status.");
    }
  };

  const onPayout = async (id) => {
    if (armedPayout !== id) {
      setArmedPayout(id);
      setTimeout(() => setArmedPayout(null), 4000);
      return;
    }
    setArmedPayout(null);
    try {
      const { data } = await axios.post(`${API}/ambassadors/${id}/payout`, {}, { withCredentials: true });
      toast.success(`Marked A$${data.amount.toFixed(2)} paid (${data.sales_count} sales)`);
      loadAmbassadors();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Payout failed.");
    }
  };

  const onSaveContract = async () => {
    setAmbContractSaving(true);
    try {
      await axios.put(`${API}/ambassadors/contract`, { text: ambContract }, { withCredentials: true });
      toast.success("Contract saved — new signups get this version");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setAmbContractSaving(false);
    }
  };

  const loadOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data.orders);
      setOrderStats({ total: data.total, revenue: data.revenue });
      setTrackingConfigured(!!data.tracking_configured);
    } catch {
      toast.error("Could not load orders.");
    }
  };

  const loadDiscounts = async () => {
    try {
      const { data } = await axios.get(`${API}/discounts`, { withCredentials: true });
      setDiscounts(data.codes);
    } catch {
      /* optional */
    }
  };

  const onCreateDiscount = async () => {
    if (!discCode.trim() || !discValue) {
      toast.error("Enter a code and a value.");
      return;
    }
    setDiscSaving(true);
    try {
      await axios.post(
        `${API}/discounts`,
        { code: discCode.trim(), kind: discKind, value: Number(discValue) },
        { withCredentials: true }
      );
      toast.success(`Code ${discCode.trim().toUpperCase()} is live.`);
      setDiscCode("");
      setDiscValue("");
      loadDiscounts();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create code.");
    } finally {
      setDiscSaving(false);
    }
  };

  const onToggleDiscount = async (code) => {
    try {
      const { data } = await axios.post(`${API}/discounts/${code}/toggle`, {}, { withCredentials: true });
      toast.success(`${code} ${data.active ? "activated" : "switched off"}.`);
      loadDiscounts();
    } catch {
      toast.error("Could not update code.");
    }
  };

  const loadReferrals = async () => {
    try {
      const { data } = await axios.get(`${API}/referrals`, { withCredentials: true });
      setReferrals(data.codes);
      setWalkupBatches(data.walkup_batches || []);
    } catch {
      /* optional */
    }
  };

  const onIssueReferral = async () => {
    if (!refEmail.trim()) {
      toast.error("Enter a customer email.");
      return;
    }
    setRefSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/referrals/issue`,
        { email: refEmail.trim() },
        { withCredentials: true }
      );
      toast.success(`Referral code ${data.code} issued.`);
      setRefEmail("");
      loadReferrals();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || "Could not issue code.");
    } finally {
      setRefSaving(false);
    }
  };

  const loadPopupLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/popup/leads`, { withCredentials: true });
      setPopupLeads(data);
    } catch {
      /* optional */
    }
  };

  const onSendPopupNudge = async () => {
    setNudgingPopup(true);
    try {
      const { data } = await axios.post(`${API}/popup/nudge`, {}, { withCredentials: true });
      toast.success(data.sent > 0 ? `${data.sent} nudge${data.sent === 1 ? "" : "s"} sent.` : "No one to nudge right now — leads need 48h without buying first.");
      loadPopupLeads();
    } catch {
      toast.error("Could not send nudges.");
    } finally {
      setNudgingPopup(false);
    }
  };

  const onDeleteReferral = async (code) => {
    try {
      await axios.delete(`${API}/referrals/${code}`, { withCredentials: true });
      toast.success(`Code ${code} deleted.`);
      loadReferrals();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete code.");
    }
  };

  const onGenerateWalkup = async () => {
    const count = parseInt(walkupCount, 10);
    if (!count || count < 1 || count > 50) {
      toast.error("Enter between 1 and 50 cards.");
      return;
    }
    setWalkupSaving(true);
    try {
      const res = await axios.post(
        `${API}/referrals/walkup`,
        { count, label: walkupLabel.trim() },
        { withCredentials: true, responseType: "blob" }
      );
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "breethe-referral-cards.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${count} referral card${count === 1 ? "" : "s"} downloaded — print A5 landscape.`);
      setWalkupLabel("");
      loadReferrals();
    } catch {
      toast.error("Could not generate cards — try again.");
    } finally {
      setWalkupSaving(false);
    }
  };

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/all`, { withCredentials: true });
      setReviews(data.reviews);
    } catch {
      /* optional */
    }
  };

  const onToggleReview = async (id) => {
    try {
      const { data } = await axios.post(`${API}/reviews/${id}/approve`, {}, { withCredentials: true });
      toast.success(data.approved ? "Review live on the site." : "Review hidden.");
      loadReviews();
    } catch {
      toast.error("Could not update review.");
    }
  };

  const onDeleteReview = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, { withCredentials: true });
      toast.success("Review deleted.");
      loadReviews();
    } catch {
      toast.error("Could not delete review.");
    }
  };

  const loadTraffic = async (window = trafficWindow) => {
    try {
      const { data } = await axios.get(`${API}/analytics/visitors?window=${window}`, { withCredentials: true });
      setTraffic(data);
    } catch {
      /* optional */
    }
  };

  const trafficSeriesLabel = (iso) => {
    const d = new Date(iso);
    return trafficWindow === "7d" || trafficWindow === "30d"
      ? d.toLocaleDateString("en-AU", { day: "numeric", month: "short" })
      : d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  const TRAFFIC_WINDOWS = [
    ["1h", "1H"],
    ["24h", "24H"],
    ["7d", "7D"],
    ["30d", "30D"],
  ];

  const TRAFFIC_WINDOW_LABELS = {
    "1h": "last hour",
    "24h": "last 24 hours",
    "7d": "last 7 days",
    "30d": "last 30 days",
  };

  const loadMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/contact/messages`, { withCredentials: true });
      setMessages(data.messages);
    } catch {
      /* optional */
    }
  };

  const onDeleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/contact/messages/${id}`, { withCredentials: true });
      toast.success("Message deleted.");
      loadMessages();
    } catch {
      toast.error("Could not delete message.");
    }
  };

  const loadShipCfg = async () => {
    try {
      const { data } = await axios.get(`${API}/shipping/settings`, { withCredentials: true });
      setShipCfg(data);
      setShipFreeAll(!!data.free_all);
      setShipFee((data.fee_cents / 100).toFixed(2));
      setShipThreshold((data.free_threshold_cents / 100).toFixed(2));
    } catch {
      /* optional */
    }
  };

  const loadFunnel = async (window = funnelWindow) => {
    try {
      const { data } = await axios.get(`${API}/analytics/funnel?window=${window}`, { withCredentials: true });
      setFunnel(data);
    } catch {
      /* optional */
    }
  };

  const onSaveShipCfg = async () => {
    const fee = parseFloat(shipFee);
    const thr = parseFloat(shipThreshold);
    if (isNaN(fee) || fee < 0 || isNaN(thr) || thr < 0) {
      toast.error("Enter valid dollar amounts.");
      return;
    }
    setSavingShipCfg(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/shipping/settings`,
        { free_all: shipFreeAll, fee, free_threshold: thr },
        { withCredentials: true }
      );
      setShipCfg(data);
      toast.success(
        shipFreeAll
          ? "Free postage is ON — website and checkout updated."
          : `Postage set to $${fee.toFixed(2)} flat, free over $${thr.toFixed(2)}.`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingShipCfg(false);
    }
  };

  useEffect(() => {
    if (auth) {
      loadFounders();
      loadBlasts();
      loadOrders();
      loadAmbassadors();
      loadDiscounts();
      loadReferrals();
      loadReviews();
      loadTraffic("24h");
      loadMessages();
      loadShipCfg();
      loadFunnel("30d");
      loadPopupLeads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const onShip = async (sessionId) => {
    const tracking = (trackingInputs[sessionId] || "").trim();
    if (!tracking) {
      toast.error("Add the Australia Post tracking number first.");
      return;
    }
    setShipping(sessionId);
    try {
      await axios.post(
        `${API}/orders/${sessionId}/ship`,
        { tracking_number: tracking },
        { withCredentials: true }
      );
      toast.success("Marked posted — tracking email sent.");
      setEditingShip((m) => ({ ...m, [sessionId]: false }));
      loadOrders();
    } catch {
      toast.error("Could not mark as posted.");
    } finally {
      setShipping(null);
    }
  };

  const onCampaign = async (kind) => {
    if (sendingCampaign) return;
    if (armed !== kind) {
      setArmed(kind);
      setTimeout(() => setArmed(null), 4000);
      return;
    }
    setArmed(null);
    setSendingCampaign(kind);
    setCampaignResult("");
    try {
      const { data } = await axios.post(`${API}/email/${kind}`, {}, { withCredentials: true });
      setCampaignResult(
        `${kind === "blast" ? "Blast" : "Nudge"} sent to ${data.sent}/${data.total} founders${data.failed ? ` — ${data.failed} failed` : ""}`
      );
      toast.success(`${kind === "blast" ? "Blast" : "Nudge"} sent to ${data.sent} founders`);
      loadBlasts();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 503) {
        setCampaignResult("Email key not configured — add RESEND_API_KEY first.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Campaign failed");
      }
    } finally {
      setSendingCampaign(null);
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setAuth(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* cookie cleared client-side anyway */
    }
    setAuth(false);
    setEntries([]);
    setTotal(0);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="font-plex text-xs uppercase tracking-[0.3em] text-white/50">Checking access…</p>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white font-manrope flex items-center justify-center px-6 grid-lines">
        <div className="w-full max-w-md border border-white/10 bg-[#121212] p-8 md:p-10">
          <img src="/breethe-logo.webp" alt="Breethe" className="h-7 w-auto" />
          <p className="mt-6 font-plex text-xs uppercase tracking-[0.25em] text-volt">Founders HQ</p>
          <h1 className="mt-2 font-anton uppercase text-3xl md:text-4xl leading-[0.9]">
            Admin only<span className="text-volt">.</span>
          </h1>

          <form data-testid="admin-login-form" onSubmit={onLogin} className="mt-8 space-y-4">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="EMAIL"
              data-testid="admin-email-input"
              className="w-full h-14 bg-transparent border border-white/20 focus:border-volt px-5 font-plex text-sm uppercase tracking-[0.15em] placeholder:text-white/30 text-white outline-none transition-colors duration-300"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="PASSWORD"
              data-testid="admin-password-input"
              className="w-full h-14 bg-transparent border border-white/20 focus:border-volt px-5 font-plex text-sm tracking-[0.15em] placeholder:text-white/30 placeholder:uppercase text-white outline-none transition-colors duration-300"
            />
            {error && (
              <p data-testid="admin-login-error" className="font-plex text-xs uppercase tracking-[0.15em] text-volt">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full h-14 bg-volt text-black font-anton uppercase tracking-wider text-base transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? "Unlocking…" : "Unlock Dashboard"}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-4">
            <span className="flex-1 h-px bg-white/10" />
            <span className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">or</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <button
            type="button"
            onClick={onGoogle}
            data-testid="admin-google-login-button"
            className="mt-6 w-full h-14 border border-white/25 text-white font-anton uppercase tracking-wider text-base transition-[background-color,color,transform] duration-200 hover:bg-white hover:text-black active:scale-[0.98]"
          >
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  const topValue = (key) => {
    if (!entries.length) return "—";
    const counts = {};
    entries.forEach((e) => {
      counts[e[key]] = (counts[e[key]] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  };
  const usingNowPct = entries.length
    ? Math.round((entries.filter((e) => e.used_before === "Using them now").length / entries.length) * 100)
    : 0;
  const boughtEmails = new Set(orders.map((o) => o.email));

  // Referrals list: handout-card codes live under their batches above, so the
  // list defaults to invite/reward/popup codes only; search covers everything
  const refFiltered = referrals.filter((r) => {
    if (!showWalkupCodes && r.kind === "walkup") return false;
    if (!refSearch.trim()) return true;
    const hay = `${r.code} ${r.owner_email || ""} ${r.used_by || ""} ${r.batch_label || ""}`.toLowerCase();
    return hay.includes(refSearch.trim().toLowerCase());
  });

  const toggleBatch = (batchId) =>
    setOpenBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });

  const inputCls =
    "w-full h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-sm text-white outline-none transition-colors duration-300 placeholder:text-white/30";
  const textareaCls =
    "w-full bg-transparent border border-white/20 focus:border-volt px-4 py-3 font-plex text-sm text-white outline-none transition-colors duration-300 placeholder:text-white/30";
  const labelCls = "block font-plex text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2";

  const stats = [
    { label: "Total Founders", value: String(total), testid: "stat-total-founders" },
    { label: "Top Price Point", value: topValue("price_point"), testid: "stat-top-price" },
    { label: "Top Primary Use", value: topValue("primary_use"), testid: "stat-top-use" },
    { label: "Using Strips Now", value: `${usingNowPct}%`, testid: "stat-using-now" },
    { label: "Orders", value: String(orderStats.total), testid: "stat-total-orders" },
    { label: "Revenue", value: `A$${orderStats.revenue.toFixed(2)}`, testid: "stat-revenue" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-white font-manrope">
      <header className="sticky top-0 z-40 bg-[#0A0A0A] border-b border-white/10 px-4 md:px-10 h-16 md:h-20 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 shrink-0">
          <img src="/breethe-logo.webp" alt="Breethe" className="h-6 w-auto" />
          <span className="hidden sm:inline font-plex text-[10px] uppercase tracking-[0.3em] text-volt">Founders HQ</span>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <button
            onClick={() => navigate("/admin/email-suite", { state: { user: auth } })}
            data-testid="email-suite-link"
            className="h-10 px-3 md:px-4 bg-volt text-black flex items-center gap-2 font-plex text-xs uppercase tracking-[0.2em] transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
          >
            <Mail size={14} /> Email suite
          </button>
          <button
            onClick={onSyncShipping}
            disabled={syncingShip}
            data-testid="sync-addresses-button"
            className="hidden md:block h-10 px-4 border border-white/15 font-plex text-xs uppercase tracking-[0.2em] text-white/60 hover:text-volt hover:border-volt transition-colors active:scale-95 disabled:opacity-60"
          >
            {syncingShip ? "Syncing…" : "Sync addresses"}
          </button>
          <button
            onClick={() => {
              loadFounders();
              loadOrders();
              loadAmbassadors();
            }}
            data-testid="admin-refresh-button"
            className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/60 hover:text-volt hover:border-volt transition-colors active:scale-95"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={dataLoading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="h-10 px-3 md:px-4 border border-white/15 flex items-center gap-2 font-plex text-xs uppercase tracking-[0.2em] text-white/60 hover:text-volt hover:border-volt transition-colors active:scale-95"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <nav
        data-testid="admin-sidebar"
        className="hidden md:flex fixed left-0 top-20 bottom-0 w-64 flex-col gap-1 border-r border-white/10 bg-[#0A0A0A] p-6 z-30"
      >
        {TABS.map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => goTab(id)}
            data-testid={`admin-tab-${id}`}
            className={`flex items-center gap-3 h-11 px-4 font-plex text-xs uppercase tracking-[0.2em] transition-colors duration-150 active:scale-[0.98] ${
              tab === id ? "bg-volt text-black" : "text-white/50 hover:text-white hover:bg-white/5"
            }`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </nav>

      <div className="md:pl-64">

      <main className="px-6 md:px-10 py-10 md:py-14 pb-28 max-w-[1600px] mx-auto">
        <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">
          Signed in as {auth.email}
        </p>
        <h1 className="mt-2 font-anton uppercase text-4xl md:text-6xl leading-[0.9]">
          {TABS.find(([id]) => id === tab)?.[1]}<span className="text-volt">.</span>
        </h1>

        <div data-testid="admin-panel-stats" className={tab === "overview" ? "" : "hidden"}>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {stats.map((s) => (
            <div key={s.label} data-testid={s.testid} className="border border-white/10 bg-[#121212] p-5 md:p-6">
              <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">{s.label}</p>
              <p className="mt-3 font-anton text-2xl md:text-4xl leading-none">{s.value}</p>
            </div>
          ))}
        </div>
        </div>

        <div data-testid="admin-panel-postage" className={tab === "config" ? "" : "hidden"}>
        <div data-testid="postage-settings-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <Truck size={22} className="text-volt" /> Postage
          </p>
          <p className="mt-2 text-white/50 font-light text-sm">
            Changes the website messaging and the Stripe checkout shipping charge. Subscriptions always ship free.
          </p>
          <div className="mt-6 flex flex-col md:flex-row md:items-end gap-6">
            <div>
              <label className={labelCls}>Free postage</label>
              <button
                onClick={() => setShipFreeAll(!shipFreeAll)}
                data-testid="postage-free-toggle"
                className={`h-12 px-6 font-anton uppercase tracking-wider text-sm transition-[background-color,color,border-color,transform] duration-200 active:scale-[0.98] ${
                  shipFreeAll
                    ? "bg-volt text-black"
                    : "border border-white/15 text-white/60 hover:border-volt hover:text-volt"
                }`}
              >
                {shipFreeAll ? "On — every order ships free" : "Off"}
              </button>
            </div>
            <div>
              <label className={labelCls} htmlFor="postage-fee-input">Flat rate (A$)</label>
              <input
                id="postage-fee-input"
                data-testid="postage-fee-input"
                type="number"
                min="0"
                step="0.01"
                value={shipFee}
                onChange={(e) => setShipFee(e.target.value)}
                disabled={shipFreeAll}
                className={`${inputCls} md:w-40 disabled:opacity-40`}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="postage-threshold-input">Free over (A$)</label>
              <input
                id="postage-threshold-input"
                data-testid="postage-threshold-input"
                type="number"
                min="0"
                step="0.01"
                value={shipThreshold}
                onChange={(e) => setShipThreshold(e.target.value)}
                disabled={shipFreeAll}
                className={`${inputCls} md:w-40 disabled:opacity-40`}
              />
            </div>
            <button
              onClick={onSaveShipCfg}
              disabled={savingShipCfg}
              data-testid="postage-save-button"
              className="h-12 px-8 bg-volt text-black font-anton uppercase tracking-wider text-sm hover:bg-volt-dark transition-[background-color,transform] duration-200 active:scale-[0.98] disabled:opacity-60"
            >
              {savingShipCfg ? "Saving…" : "Save postage"}
            </button>
          </div>
          {shipCfg && (
            <p data-testid="postage-current-note" className="mt-4 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
              Current: {shipCfg.free_all
                ? "free on every order"
                : `$${(shipCfg.fee_cents / 100).toFixed(2)} flat — free over $${(shipCfg.free_threshold_cents / 100).toFixed(2)}`}
            </p>
          )}
        </div>
        </div>

        <div data-testid="admin-panel-traffic" className={tab === "overview" ? "" : "hidden"}>
        {traffic && (
          <div data-testid="analytics-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
                <BarChart3 size={22} className="text-volt" /> Site traffic
              </p>
              <div className="flex gap-2">
                {TRAFFIC_WINDOWS.map(([w, label]) => (
                  <button
                    key={w}
                    data-testid={`traffic-window-${w}`}
                    onClick={() => { setTrafficWindow(w); loadTraffic(w); }}
                    className={`h-9 px-4 font-plex text-[11px] uppercase tracking-[0.2em] transition-[background-color,color,border-color] duration-200 active:scale-95 ${
                      trafficWindow === w
                        ? "bg-volt text-black"
                        : "border border-white/15 text-white/60 hover:text-volt hover:border-volt"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 max-w-xl">
              {[
                ["Unique visitors", traffic.visitors, "traffic-tile-visitors"],
                ["Page views", traffic.views, "traffic-tile-views"],
              ].map(([label, value, testid]) => (
                <div key={testid} data-testid={testid} className="border border-white/10 p-4">
                  <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">
                    {label} — {TRAFFIC_WINDOW_LABELS[trafficWindow]}
                  </p>
                  <p className="mt-2 font-anton text-2xl md:text-3xl leading-none">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 border border-white/10 p-4">
              <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                Page views — {TRAFFIC_WINDOW_LABELS[trafficWindow]}
              </p>
              <div data-testid="analytics-chart" className="flex items-end gap-1 h-24">
                {traffic.series.map((d) => {
                  const max = Math.max(...traffic.series.map((x) => x.views), 1);
                  return (
                    <div
                      key={d.t}
                      className="flex-1 flex flex-col justify-end h-full"
                      title={`${trafficSeriesLabel(d.t)}: ${d.views} views · ${d.visitors} visitors`}
                    >
                      <div
                        className={`w-full ${d.views ? "bg-volt" : "bg-white/10"}`}
                        style={{ height: `${Math.max((d.views / max) * 100, 4)}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex justify-between font-plex text-[9px] uppercase tracking-[0.15em] text-white/30">
                <span>{traffic.series[0] ? trafficSeriesLabel(traffic.series[0].t) : ""}</span>
                <span>{traffic.series.length ? trafficSeriesLabel(traffic.series[traffic.series.length - 1].t) : ""}</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                ["Pages", traffic.pages, "analytics-pages"],
                ["Referrers", traffic.referrers, "analytics-referrers"],
                ["Countries", traffic.countries, "analytics-countries"],
                ["Devices", traffic.devices, "analytics-devices"],
              ].map(([title, rows, testid]) => (
                <div key={testid} data-testid={testid} className="border border-white/10 p-4">
                  <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                    {title} — unique visitors
                  </p>
                  {rows.length === 0 && (
                    <p className="font-plex text-[10px] uppercase tracking-[0.2em] text-white/25">
                      No data yet
                    </p>
                  )}
                  {rows.map((r) => {
                    const max = rows[0]?.visitors || 1;
                    return (
                      <div key={r.key} className="mb-2">
                        <div className="flex justify-between font-plex text-[11px] uppercase tracking-[0.15em]">
                          <span className="text-white/80 truncate mr-2">{r.key}</span>
                          <span className="text-white/40 whitespace-nowrap">{r.visitors} · {r.views}</span>
                        </div>
                        <div className="mt-1 h-1 bg-white/10">
                          <div className="h-full bg-volt" style={{ width: `${(r.visitors / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <p className="mt-3 font-plex text-[10px] uppercase tracking-[0.2em] text-white/30">
              Numbers shown as unique visitors · total views
            </p>
          </div>
        )}
        </div>

        <div data-testid="admin-panel-funnel" className={tab === "overview" ? "" : "hidden"}>
        {funnel && (
          <div data-testid="funnel-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
                <TrendingUp size={22} className="text-volt" /> Conversion funnel
              </p>
              <div className="flex gap-2">
                {[["1h", "1H"], ["24h", "24H"], ["7d", "7D"], ["30d", "30D"]].map(([w, label]) => (
                  <button
                    key={w}
                    data-testid={`funnel-window-${w}`}
                    onClick={() => { setFunnelWindow(w); loadFunnel(w); }}
                    className={`h-9 px-4 font-plex text-[11px] uppercase tracking-[0.2em] transition-[background-color,color,border-color] duration-200 active:scale-95 ${
                      funnelWindow === w
                        ? "bg-volt text-black"
                        : "border border-white/15 text-white/60 hover:text-volt hover:border-volt"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 space-y-4">
              {[
                { label: "Site visitors", value: funnel.visitors, testid: "funnel-step-visitors", note: `${funnel.page_views} page views` },
                { label: "Reached checkout", value: funnel.checkout_started, testid: "funnel-step-checkout", note: `${funnel.visit_to_checkout_pct}% of visitors` },
                { label: "Purchased", value: funnel.checkout_paid, testid: "funnel-step-purchased", note: `${funnel.checkout_to_paid_pct}% of checkouts` },
              ].map((step) => {
                const max = Math.max(funnel.visitors, funnel.checkout_started, 1);
                const width = step.value > 0 ? Math.max((step.value / max) * 100, 3) : 0;
                return (
                  <div key={step.testid} data-testid={step.testid}>
                    <div className="flex justify-between font-plex text-[11px] uppercase tracking-[0.15em]">
                      <span className="text-white/80">{step.label}</span>
                      <span className="text-white/40">{step.value} · {step.note}</span>
                    </div>
                    <div className="mt-1 h-2 bg-white/10">
                      <div className="h-full bg-volt transition-[width] duration-500" style={{ width: `${width}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                ["Overall conversion", `${funnel.visit_to_paid_pct}%`, "funnel-overall-conversion"],
                ["Checkout completion", `${funnel.checkout_to_paid_pct}%`, "funnel-checkout-completion"],
                ["Abandoned checkouts", `${funnel.abandoned} (${funnel.abandonment_pct}%)`, "funnel-abandoned"],
                ["Still in progress", funnel.in_progress, "funnel-in-progress"],
                ["Recovered by email", `${funnel.recovered} (${funnel.recovery_pct}%)`, "funnel-recovered"],
              ].map(([label, value, testid]) => (
                <div key={testid} data-testid={testid} className="border border-white/10 p-4">
                  <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</p>
                  <p className="mt-2 font-anton text-2xl md:text-3xl leading-none">{value}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 font-plex text-[10px] uppercase tracking-[0.2em] text-white/30">
              Abandoned = reached checkout but never paid (checkout links expire after 24h). In progress = checkout opened in the last 24 hours. Recovered = paid after a recovery email ({funnel.abandon_emailed} sent in this period).
            </p>

            {funnel.by_plan.length > 0 && (
              <div className="mt-6 border border-white/10 p-4">
                <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">By product</p>
                {funnel.by_plan.map((p) => (
                  <div
                    key={p.lookup_key}
                    data-testid={`funnel-plan-${p.lookup_key}`}
                    className="flex justify-between py-2 border-b border-white/5 last:border-0 font-plex text-[11px] uppercase tracking-[0.15em]"
                  >
                    <span className="text-white/80">
                      {p.lookup_key === "n001_pack"
                        ? "The Pack"
                        : p.lookup_key === "n001_monthly"
                          ? "Subscription"
                          : p.lookup_key === "n001_trial"
                            ? "First pack (intro)"
                            : p.lookup_key}
                    </span>
                    <span className="text-white/40">
                      {p.started} checkout{p.started === 1 ? "" : "s"} · {p.paid} paid · {p.conversion_pct}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>

        <div data-testid="admin-panel-campaigns" className={tab === "marketing" ? "" : "hidden"}>
        <div data-testid="campaigns-card" className="mt-10 border border-volt/60 bg-volt/5 p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <Megaphone size={22} className="text-volt" /> Campaigns
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-white/10 bg-[#121212] p-6 flex flex-col">
              <p className="font-anton uppercase text-lg">Drop-day blast</p>
              <p className="mt-2 text-white/60 font-light text-sm flex-1">
                Announce the launch to every founder at once — all {total}{" "}
                {total === 1 ? "founder" : "founders"}. Uses the Drop-Day Blast template.
              </p>
              <button
                onClick={() => onCampaign("blast")}
                disabled={!!sendingCampaign}
                data-testid="blast-send-button"
                className={campaignBtnCls(armed === "blast")}
              >
                {sendingCampaign === "blast" ? "Blasting…" : armed === "blast" ? "Sure? Click again" : "Send blast"}
              </button>
            </div>
            <div className="border border-white/10 bg-[#121212] p-6 flex flex-col">
              <p className="font-anton uppercase text-lg">Abandoned signup nudge</p>
              <p className="mt-2 text-white/60 font-light text-sm flex-1">
                Remind founders who joined but haven't bought. Once checkout is live, buyers are
                skipped automatically. Uses the Nudge Email template.
              </p>
              <button
                onClick={() => onCampaign("nudge")}
                disabled={!!sendingCampaign}
                data-testid="nudge-send-button"
                className={campaignBtnCls(armed === "nudge")}
              >
                {sendingCampaign === "nudge" ? "Nudging…" : armed === "nudge" ? "Sure? Click again" : "Send nudge"}
              </button>
            </div>
          </div>
          {campaignResult && (
            <p data-testid="campaign-result" className="mt-4 font-plex text-xs uppercase tracking-[0.2em] text-volt">
              {campaignResult}
            </p>
          )}
          {blasts.length > 0 && (
            <ul data-testid="blast-history" className="mt-4 space-y-1 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
              {blasts.map((b) => (
                <li key={b.id}>
                  {b.type === "nudge"
                    ? "Nudge"
                    : b.type === "blast"
                      ? "Blast"
                      : b.type?.startsWith("manual:")
                        ? `Manual — ${b.type.split(":")[1]}`
                        : b.type === "custom"
                          ? `Custom — ${b.subject || "one-off"}`
                          : b.type}{" "}
                  — {b.sent}/{b.total} sent
                  {b.failed ? `, ${b.failed} failed` : ""} —{" "}
                  {new Date(b.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}{" "}
                  {new Date(b.created_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                </li>
              ))}
            </ul>
          )}
        </div>
        </div>

        <div data-testid="admin-panel-emailsuite" className={tab === "marketing" ? "" : "hidden"}>
        <div data-testid="email-studio" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
              <Mail size={22} className="text-volt" /> Email suite
            </p>
            <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-xl">
              Templates moved to the Email Suite — edit every email the site sends, fire any
              template at individual customers or email captures, or compose a one-off in the
              same Breethe design.
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/email-suite", { state: { user: auth } })}
            data-testid="email-suite-open-button"
            className="h-12 px-8 bg-volt text-black font-anton uppercase tracking-wider text-sm transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 shrink-0"
          >
            Open Email Suite
          </button>
        </div>
        </div>

        <div data-testid="admin-panel-discounts" className={tab === "config" ? "" : "hidden"}>
        <div data-testid="discounts-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <Tag size={22} className="text-volt" /> Discount codes
          </p>
          <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-xl">
            Create a code, share it anywhere. Customers apply it at checkout — or type it into the
            code box on the pricing section.
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3">
            <input
              value={discCode}
              onChange={(e) => setDiscCode(e.target.value.toUpperCase())}
              placeholder="CODE — E.G. FOUNDERS50"
              data-testid="discount-code-input"
              className={`${inputCls} md:col-span-4 uppercase`}
            />
            <div className="md:col-span-3 flex gap-2">
              {[
                ["percent", "% Off"],
                ["fixed", "A$ Off"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`discount-kind-${key}`}
                  onClick={() => setDiscKind(key)}
                  className={`flex-1 h-12 font-anton uppercase tracking-wider text-sm transition-[background-color,color,border-color,transform] duration-200 active:scale-95 ${
                    discKind === key
                      ? "bg-volt text-black"
                      : "border border-white/20 text-white/60 hover:text-white hover:border-white/50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <input
              type="number"
              min="1"
              value={discValue}
              onChange={(e) => setDiscValue(e.target.value)}
              placeholder={discKind === "percent" ? "E.G. 20" : "E.G. 5"}
              data-testid="discount-value-input"
              className={`${inputCls} md:col-span-2`}
            />
            <button
              onClick={onCreateDiscount}
              disabled={discSaving}
              data-testid="discount-create-button"
              className="md:col-span-3 h-12 bg-volt text-black font-anton uppercase tracking-wider text-sm transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60"
            >
              {discSaving ? "Creating…" : "Create Code"}
            </button>
          </div>

          {discounts.length > 0 && (
            <ul data-testid="discount-list" className="mt-6 border-t border-white/10">
              {discounts.map((d, i) => (
                <li
                  key={d.id}
                  data-testid={`discount-row-${i + 1}`}
                  className="border-b border-white/10 py-4 flex flex-wrap items-center gap-x-6 gap-y-2"
                >
                  <span className="font-plex text-sm tracking-[0.15em] text-volt">{d.code}</span>
                  <span className="text-white/80 text-sm">
                    {d.kind === "percent" ? `${d.value}% off` : `A$${d.value} off`}
                  </span>
                  <span className="font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                    Used {d.times_used}×
                  </span>
                  <button
                    onClick={() => onToggleDiscount(d.code)}
                    data-testid={`discount-toggle-${i + 1}`}
                    className={`ml-auto h-9 px-4 font-anton uppercase text-xs tracking-wider transition-[background-color,color,transform,border-color] duration-200 active:scale-95 ${
                      d.active
                        ? "border border-volt/60 text-volt hover:bg-volt hover:text-black"
                        : "border border-white/20 text-white/40 hover:text-white"
                    }`}
                  >
                    {d.active ? "Active" : "Off"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        </div>

        <div data-testid="admin-panel-referrals" className={tab === "referrals" ? "" : "hidden"}>
        <div data-testid="referrals-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <Megaphone size={22} className="text-volt" /> Referral codes
          </p>
          <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-xl">
            Every first-time buyer automatically gets a single-use code to share — their mate gets 10%
            off a first order, and the referrer earns 10% off their next one (90-day expiry, max 5
            rewards each). Issue one manually below, or print a pack flyer with QR code for any code.
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3">
            <input
              type="email"
              value={refEmail}
              onChange={(e) => setRefEmail(e.target.value)}
              placeholder="CUSTOMER EMAIL — E.G. MATE@EMAIL.COM"
              data-testid="referral-issue-email-input"
              className={`${inputCls} md:col-span-6`}
            />
            <button
              onClick={onIssueReferral}
              disabled={refSaving}
              data-testid="referral-issue-button"
              className="md:col-span-3 h-12 bg-volt text-black font-anton uppercase tracking-wider text-sm transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60"
            >
              {refSaving ? "Issuing…" : "Issue Code"}
            </button>
          </div>

          <div data-testid="walkup-card" className="mt-8 border border-volt/40 bg-volt/5 p-5">
            <p className="font-plex text-xs uppercase tracking-[0.25em] text-volt">
              In-person handout cards — no email needed
            </p>
            <p className="mt-2 text-white/50 font-light text-sm max-w-2xl">
              Generate a batch of cards and download a print-ready PDF — A5 landscape, two cards per
              page, each card page followed by a branded back page so you can print double-sided
              (flip on either edge works). Cut down the centre; there are no markings on the cards.
              Every card carries two single-use 10% codes:
              one for the person you hand it to, one for a mate of theirs. No email is needed to use a
              code; the buyer's email is captured when they pay, and each code burns after one use.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={walkupLabel}
                onChange={(e) => setWalkupLabel(e.target.value)}
                placeholder="BATCH LABEL — E.G. PARKRUN OCT 18"
                data-testid="walkup-label-input"
                className={`${inputCls} sm:flex-1`}
              />
              <input
                type="number"
                min="1"
                max="50"
                value={walkupCount}
                onChange={(e) => setWalkupCount(e.target.value)}
                placeholder="HOW MANY CARDS?"
                data-testid="walkup-count-input"
                className={`${inputCls} sm:w-44`}
              />
              <button
                onClick={onGenerateWalkup}
                disabled={walkupSaving}
                data-testid="walkup-generate-button"
                className="h-12 px-8 bg-volt text-black font-anton uppercase tracking-wider text-sm transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60"
              >
                {walkupSaving ? "Generating…" : "Generate & download PDF"}
              </button>
            </div>

            {walkupBatches.length > 0 && (
              <div data-testid="walkup-batches" className="mt-5 border-t border-white/10 pt-4 space-y-3">
                {walkupBatches.map((b, i) => (
                  <div key={b.batch_id} data-testid={`walkup-batch-${i + 1}`}>
                    <button
                      onClick={() => toggleBatch(b.batch_id)}
                      data-testid={`walkup-batch-toggle-${i + 1}`}
                      className="w-full flex justify-between items-center font-plex text-[11px] uppercase tracking-[0.15em] text-left"
                    >
                      <span className="text-white/80">
                        {b.label || `Batch ${b.batch_id}`}
                        <span className="text-white/30"> · {new Date(b.created_at).toLocaleDateString("en-AU")}</span>
                      </span>
                      <span className="text-white/40 flex items-center gap-2">
                        {b.used}/{b.total} converted · {b.used_pct}%
                        {b.mates_used > 0 && ` · ${b.mates_used} mate${b.mates_used === 1 ? "" : "s"}`}
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${openBatches.has(b.batch_id) ? "rotate-180" : ""}`}
                        />
                      </span>
                    </button>
                    {openBatches.has(b.batch_id) && (
                      <>
                        <div className="mt-2 h-1.5 bg-white/10">
                          <div
                            className="h-full bg-volt transition-[width] duration-500"
                            style={{ width: `${b.used_pct}%` }}
                          />
                        </div>
                        {b.emails?.length > 0 && (
                          <p
                            data-testid={`walkup-batch-emails-${i + 1}`}
                            className="mt-2 font-plex text-[10px] tracking-[0.08em] text-white/50 normal-case break-all"
                          >
                            {b.emails.map((e) => `${e.email}${e.role === "mate" ? " (mate)" : ""}`).join("  ·  ")}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                value={refSearch}
                onChange={(e) => setRefSearch(e.target.value)}
                placeholder="SEARCH CODES, OWNERS, BUYERS…"
                data-testid="referral-search-input"
                className={`${inputCls} pl-11`}
              />
            </div>
            <button
              onClick={() => setShowWalkupCodes((v) => !v)}
              data-testid="referral-walkup-toggle"
              className={`h-12 px-5 border font-anton uppercase tracking-wider text-xs transition-[background-color,color,border-color,transform] duration-200 active:scale-95 ${
                showWalkupCodes
                  ? "bg-white text-black border-white"
                  : "border-white/25 text-white/60 hover:text-white hover:border-white/50"
              }`}
            >
              {showWalkupCodes ? "Hide handout codes" : "Show handout codes"}
            </button>
          </div>

          {refFiltered.length > 0 && (
            <ul data-testid="referral-list" className="mt-6 border-t border-white/10 max-h-[60vh] overflow-y-auto">
              {refFiltered.map((r, i) => (
                <li
                  key={r.id}
                  data-testid={`referral-row-${i + 1}`}
                  className="border-b border-white/10 py-4 flex flex-wrap items-center gap-x-6 gap-y-2"
                >
                  <span className="font-plex text-sm tracking-[0.15em] text-volt">{r.code}</span>
                  <span className="font-plex text-[10px] uppercase tracking-[0.2em] text-white/60 border border-white/20 px-2 py-1">
                    {r.kind === "reward" ? "Reward" : r.kind === "walkup" ? (r.role === "mate" ? "Walkup · mate" : "Walkup · you") : r.kind === "popup" ? "Popup" : "Invite"}
                  </span>
                  <span className="text-white/60 text-sm">{r.owner_email || "In-person"}</span>
                  <span
                    data-testid={`referral-status-${i + 1}`}
                    className={`font-plex text-[10px] uppercase tracking-[0.2em] ${
                      r.used ? "text-white/40" : r.expired ? "text-volt/70" : "text-green-400"
                    }`}
                  >
                    {r.used
                      ? `Used${r.used_by ? ` by ${r.used_by}` : ""}`
                      : r.expired
                        ? "Expired"
                        : "Active"}
                  </span>
                  {r.expires_at && !r.used && (
                    <span className="font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                      Exp {new Date(r.expires_at).toLocaleDateString("en-AU")}
                    </span>
                  )}
                  <div className="ml-auto flex gap-2">
                    <button
                      onClick={() => window.open(`${API}/referrals/flyer/${r.code}`, "_blank")}
                      data-testid={`referral-flyer-${i + 1}`}
                      className="h-9 px-4 border border-white/25 text-white font-anton uppercase text-xs tracking-wider transition-[background-color,color,transform,border-color] duration-200 hover:bg-white hover:text-black active:scale-95"
                    >
                      Print Flyer
                    </button>
                    <button
                      onClick={() => onDeleteReferral(r.code)}
                      data-testid={`referral-delete-${i + 1}`}
                      className="h-9 px-4 border border-white/25 text-white/50 font-anton uppercase text-xs tracking-wider transition-[color,border-color,transform] duration-200 hover:border-volt hover:text-volt active:scale-95"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {refFiltered.length === 0 && (
            <p className="mt-6 font-plex text-[11px] uppercase tracking-[0.2em] text-white/30">
              {referrals.length === 0
                ? "No referral codes yet — they're created automatically after a first purchase"
                : "Nothing matches — clear the search or toggle handout codes"}
            </p>
          )}
        </div>
        </div>

        <div data-testid="admin-panel-ambassadors" className={tab === "ambassadors" ? "" : "hidden"}>
        <div data-testid="ambassadors-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <Star size={22} className="text-volt" /> Brand ambassadors
          </p>
          <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-2xl">
            Invite someone, they sign the agreement online and get a portal with their own 10%
            follower code. Every sale with their code earns them 15% of the product total — you
            pay them out yourself and mark it here.
          </p>

          {/* Invite */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              value={ambName}
              onChange={(e) => setAmbName(e.target.value)}
              placeholder="THEIR NAME — E.G. JACK RUNNER"
              data-testid="ambassador-name-input"
              className={inputCls}
            />
            <input
              type="email"
              value={ambEmail}
              onChange={(e) => setAmbEmail(e.target.value)}
              placeholder="THEIR EMAIL"
              data-testid="ambassador-email-input"
              className={inputCls}
            />
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => onInviteAmbassador(true)}
              disabled={ambSending}
              data-testid="ambassador-invite-button"
              className="h-12 px-8 bg-volt text-black font-anton uppercase tracking-wider text-sm transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60"
            >
              {ambSending ? "Inviting…" : "Create invite + email it"}
            </button>
            <button
              onClick={() => onInviteAmbassador(false)}
              disabled={ambSending}
              data-testid="ambassador-invite-link-button"
              className="h-12 px-6 border border-white/25 text-white font-anton uppercase tracking-wider text-sm transition-[background-color,color,transform] duration-200 hover:bg-white hover:text-black active:scale-95 disabled:opacity-60"
            >
              Just create the link
            </button>
          </div>
          {ambLink && (
            <div data-testid="ambassador-invite-result" className="mt-4 border border-volt/60 bg-volt/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="font-plex text-xs text-volt break-all flex-1">{ambLink}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(ambLink); toast.success("Invite link copied"); }}
                data-testid="ambassador-copy-link"
                className="h-9 px-4 border border-white/25 text-white font-anton uppercase text-xs tracking-wider transition-[background-color,color,transform] duration-200 hover:bg-white hover:text-black active:scale-95 shrink-0"
              >
                Copy
              </button>
            </div>
          )}
          {ambInvites.length > 0 && (
            <ul data-testid="ambassador-invite-list" className="mt-5 border-t border-white/10 max-h-64 overflow-y-auto">
              {ambInvites.map((inv, i) => (
                <li key={inv.id} data-testid={`ambassador-invite-${i + 1}`} className="border-b border-white/5 py-3 flex flex-wrap items-center gap-x-5 gap-y-1">
                  <span className="text-white text-sm">{inv.name}</span>
                  <span className="text-white/50 text-sm font-light">{inv.email}</span>
                  <span className={`ml-auto font-plex text-[10px] uppercase tracking-[0.2em] ${inv.status === "joined" ? "text-green-400" : "text-white/40"}`}>
                    {inv.status === "joined" ? "Joined" : "Invited"} · {new Date(inv.created_at).toLocaleDateString("en-AU")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Applications from the public page */}
        <div data-testid="ambassador-apps-card" className="mt-6 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl flex items-center gap-3">
            <Inbox size={20} className="text-volt" /> Applications
            {ambApps.filter((a) => a.status === "new").length > 0 && (
              <span data-testid="ambassador-apps-new-count" className="bg-volt text-black font-plex text-[10px] tracking-[0.15em] px-2 py-1">
                {ambApps.filter((a) => a.status === "new").length} new
              </span>
            )}
          </p>
          <p className="mt-2 text-white/50 font-light text-sm max-w-2xl">
            Everyone who applies at /ambassadors lands here. Check their socials, then invite the good ones.
          </p>
          {ambApps.length > 0 ? (
            <ul data-testid="ambassador-apps-list" className="mt-5 border-t border-white/10 max-h-[60vh] overflow-y-auto">
              {ambApps.map((app, i) => (
                <li key={app.id} data-testid={`ambassador-app-${i + 1}`} className="border-b border-white/5 py-4">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                    <span className="text-white text-sm font-medium">{app.name}</span>
                    <span className="text-white/50 text-sm font-light">{app.email}</span>
                    <span className="font-plex text-[10px] uppercase tracking-[0.15em] text-white/50">
                      {[app.instagram && `IG @${app.instagram}`, app.tiktok && `TT @${app.tiktok}`, app.followers && `~${app.followers}`]
                        .filter(Boolean).join(" · ") || "no socials"}
                    </span>
                    <span className={`ml-auto font-plex text-[10px] uppercase tracking-[0.2em] ${
                      app.status === "new" ? "text-volt" : app.status === "invited" ? "text-green-400" : "text-white/30"
                    }`}>
                      {app.status}
                    </span>
                  </div>
                  {app.message && (
                    <p className="mt-2 text-white/50 font-light text-sm max-w-3xl">{app.message}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {app.status !== "invited" && (
                      <button
                        onClick={() => onAppInvite(app)}
                        data-testid={`ambassador-app-invite-${i + 1}`}
                        className="h-9 px-4 bg-volt text-black font-anton uppercase text-xs tracking-wider transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
                      >
                        Invite
                      </button>
                    )}
                    {app.status === "new" && (
                      <button
                        onClick={() => onAppStatus(app.id, "reviewed")}
                        data-testid={`ambassador-app-reviewed-${i + 1}`}
                        className="h-9 px-4 border border-white/25 text-white/70 font-anton uppercase text-xs tracking-wider transition-[background-color,color,transform] duration-200 hover:bg-white hover:text-black active:scale-95"
                      >
                        Mark reviewed
                      </button>
                    )}
                    {app.status !== "declined" && app.status !== "invited" && (
                      <button
                        onClick={() => onAppStatus(app.id, "declined")}
                        data-testid={`ambassador-app-decline-${i + 1}`}
                        className="h-9 px-4 border border-white/15 text-white/40 font-anton uppercase text-xs tracking-wider transition-colors duration-200 hover:text-volt hover:border-volt active:scale-95"
                      >
                        Decline
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p data-testid="ambassador-apps-empty" className="mt-5 font-plex text-[11px] uppercase tracking-[0.2em] text-white/30">
              No applications yet — share the ambassadors page
            </p>
          )}
        </div>

        {/* Roster */}
        {ambassadors.length > 0 && (
          <div data-testid="ambassador-roster" className="mt-6 space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {ambassadors.map((a, i) => (
              <div key={a.id} data-testid={`ambassador-card-${i + 1}`} className="border border-white/10 bg-[#121212] p-6">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  <p className="font-anton uppercase text-xl">{a.name}</p>
                  <span className="font-plex text-xs text-volt tracking-[0.15em]">{a.code}</span>
                  <span className={`font-plex text-[10px] uppercase tracking-[0.2em] border px-2 py-1 ${
                    a.status === "paused" ? "text-volt border-volt/60" : a.status === "active" ? "text-green-400 border-green-400/40" : "text-white/60 border-white/20"
                  }`}>
                    {a.status === "trial" ? "30-day trial" : a.status}
                  </span>
                  <span className="text-white/40 font-light text-sm">{a.email}</span>
                </div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    ["Sales", a.sales],
                    ["Revenue", `A$${a.revenue.toFixed(2)}`],
                    ["Earned", `A$${a.commission_earned.toFixed(2)}`],
                    ["Owed", `A$${a.commission_owed.toFixed(2)}`],
                    ["Paid", `A$${a.commission_paid.toFixed(2)}`],
                  ].map(([label, value], j) => (
                    <div key={label} data-testid={`ambassador-stat-${i + 1}-${j}`} className="border border-white/10 p-3">
                      <p className="font-plex text-[9px] uppercase tracking-[0.25em] text-white/40">{label}</p>
                      <p className={`mt-1.5 font-anton text-lg ${label === "Owed" ? "text-volt" : ""}`}>{value}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  {a.starter_pack === "sent" ? (
                    <div className="flex items-center gap-3 h-10">
                      <span data-testid={`ambassador-starter-${i + 1}`} className="font-plex text-xs uppercase tracking-[0.15em] text-green-400">
                        Starter pack: Sent ✓{a.starter_tracking ? ` · ${a.starter_tracking}` : ""}
                      </span>
                      <button
                        onClick={() => onStarterToggle(a.id)}
                        data-testid={`ambassador-starter-undo-${i + 1}`}
                        className="font-plex text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-volt transition-colors"
                      >
                        Undo
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        value={starterTracking[a.id] || ""}
                        onChange={(e) => setStarterTracking((m) => ({ ...m, [a.id]: e.target.value.toUpperCase() }))}
                        placeholder="AUSPOST TRACKING #"
                        data-testid={`ambassador-tracking-input-${i + 1}`}
                        className="h-10 w-52 bg-transparent border border-white/20 focus:border-volt px-3 font-plex text-xs text-white outline-none transition-colors duration-300 placeholder:text-white/30"
                      />
                      <button
                        onClick={() => onStarterToggle(a.id)}
                        data-testid={`ambassador-starter-${i + 1}`}
                        className="h-10 px-4 border border-white/25 font-anton uppercase text-xs tracking-wider text-white/70 transition-[background-color,color,border-color,transform] duration-200 hover:bg-white hover:text-black active:scale-95"
                      >
                        Mark sent
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => onAmbStatus(a.id, a.status === "paused" ? "active" : "paused")}
                    data-testid={`ambassador-status-${i + 1}`}
                    className="h-10 px-4 border border-white/25 font-anton uppercase text-xs tracking-wider text-white/70 transition-[background-color,color,border-color,transform] duration-200 hover:bg-white hover:text-black active:scale-95"
                  >
                    {a.status === "paused" ? "Reactivate code" : "Pause code"}
                  </button>
                  <button
                    onClick={() => onPayout(a.id)}
                    disabled={!(a.commission_owed > 0)}
                    data-testid={`ambassador-payout-${i + 1}`}
                    className={`h-10 px-5 font-anton uppercase text-xs tracking-wider transition-[background-color,color,transform] duration-200 active:scale-95 disabled:opacity-40 ${
                      armedPayout === a.id ? "bg-white text-black" : "bg-volt text-black hover:bg-volt-dark"
                    }`}
                  >
                    {armedPayout === a.id ? `Really mark A$${a.commission_owed.toFixed(2)} paid?` : `Mark owed paid (A$${a.commission_owed.toFixed(2)})`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contract editor */}
        <div data-testid="ambassador-contract-card" className="mt-6 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl flex items-center gap-3">
            <MessageSquare size={20} className="text-volt" /> Ambassador agreement
          </p>
          <p className="mt-2 text-white/50 font-light text-sm max-w-2xl">
            This is the contract new ambassadors read and sign when they join. Ambassadors who
            already signed keep the version they signed.
          </p>
          <textarea
            rows={14}
            value={ambContract}
            onChange={(e) => setAmbContract(e.target.value)}
            data-testid="ambassador-contract-input"
            className="mt-5 w-full bg-[#0A0A0A] border border-white/20 focus:border-volt px-4 py-3 font-plex text-sm text-white/80 outline-none transition-colors duration-300"
          />
          <button
            onClick={onSaveContract}
            disabled={ambContractSaving}
            data-testid="ambassador-contract-save"
            className="mt-4 h-12 px-8 bg-volt text-black font-anton uppercase tracking-wider text-sm transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60"
          >
            {ambContractSaving ? "Saving…" : "Save agreement"}
          </button>
        </div>
        </div>

        <div data-testid="admin-panel-leads" className={tab === "marketing" ? "" : "hidden"}>
        {popupLeads && (
          <div data-testid="popup-leads-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
                <Tag size={22} className="text-volt" /> Email capture <span className="text-white/40">(10% popup)</span>
              </p>
              <button
                onClick={onSendPopupNudge}
                disabled={nudgingPopup}
                data-testid="popup-nudge-button"
                className="h-11 px-6 border border-volt text-volt font-anton uppercase text-sm tracking-wider transition-[background-color,color,transform] duration-200 hover:bg-volt hover:text-black active:scale-95 disabled:opacity-60"
              >
                {nudgingPopup ? "Sending…" : "Send nudges"}
              </button>
            </div>
            <p className="mt-2 text-white/50 font-light text-sm max-w-2xl">
              Emails captured by the 10%-off popup. The nudge emails anyone who hasn't bought or used
              their code after 48 hours — once only per lead.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4 max-w-lg">
              {[
                ["Leads", popupLeads.total, "popup-total"],
                ["Converted", popupLeads.converted, "popup-converted"],
                ["Waiting", popupLeads.total - popupLeads.converted, "popup-waiting"],
              ].map(([label, value, testid]) => (
                <div key={testid} data-testid={testid} className="border border-white/10 p-4">
                  <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</p>
                  <p className="mt-2 font-anton text-2xl md:text-3xl leading-none">{value}</p>
                </div>
              ))}
            </div>
            {popupLeads.leads.length > 0 && (
              <ul data-testid="popup-lead-list" className="mt-6 border-t border-white/10">
                {popupLeads.leads.map((l, i) => (
                  <li
                    key={l.email}
                    data-testid={`popup-lead-${i + 1}`}
                    className="border-b border-white/10 py-3 flex flex-wrap items-center gap-x-6 gap-y-1"
                  >
                    <span className="text-white/80 text-sm">{l.email}</span>
                    <span className="font-plex text-xs tracking-[0.15em] text-volt">{l.code}</span>
                    <span className="font-plex text-[10px] uppercase tracking-[0.2em] text-white/30">
                      {new Date(l.created_at).toLocaleDateString("en-AU")}
                    </span>
                    <span
                      data-testid={`popup-lead-status-${i + 1}`}
                      className={`ml-auto font-plex text-[10px] uppercase tracking-[0.2em] ${
                        l.converted ? "text-green-400" : l.nudge_sent_at ? "text-volt/80" : "text-white/40"
                      }`}
                    >
                      {l.converted
                        ? l.code_used
                          ? "Code used"
                          : "Bought"
                        : l.nudge_sent_at
                          ? "Nudged"
                          : "Waiting"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {popupLeads.leads.length === 0 && (
              <p className="mt-6 font-plex text-[11px] uppercase tracking-[0.2em] text-white/30">
                No popup signups yet — the popup shows once per visitor, 7 seconds in
              </p>
            )}
          </div>
        )}
        </div>

        <div data-testid="admin-panel-reviews" className={tab === "inbox" ? "" : "hidden"}>
        <div data-testid="reviews-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <MessageSquare size={22} className="text-volt" /> Customer reviews
          </p>
          <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-xl">
            Buyers get a review-request email 5 days after you mark an order posted. New reviews land
            here hidden — approve the good ones to publish them on the site as verified buyer reviews.
          </p>

          {reviews.length > 0 ? (
            <ul data-testid="review-list" className="mt-6 border-t border-white/10">
              {reviews.map((r, i) => (
                <li
                  key={r.id}
                  data-testid={`review-row-${i + 1}`}
                  className="border-b border-white/10 py-4 flex flex-wrap items-start gap-x-6 gap-y-2"
                >
                  <span className="font-plex text-sm text-volt tracking-[0.2em] w-20 shrink-0">
                    {"★".repeat(r.rating)}
                    <span className="text-white/20">{"★".repeat(5 - r.rating)}</span>
                  </span>
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-white/90 font-light text-sm leading-relaxed">"{r.text}"</p>
                    <p className="mt-1 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                      {r.name} · {r.email} · {new Date(r.created_at).toLocaleDateString("en-AU")}
                    </p>
                  </div>
                  <span
                    data-testid={`review-status-${i + 1}`}
                    className={`font-plex text-[10px] uppercase tracking-[0.2em] ${
                      r.approved ? "text-green-400" : "text-white/40"
                    }`}
                  >
                    {r.approved ? "Live" : "Hidden"}
                  </span>
                  <div className="flex gap-2 ml-auto">
                    <button
                      onClick={() => onToggleReview(r.id)}
                      data-testid={`review-approve-${i + 1}`}
                      className={`h-9 px-4 font-anton uppercase text-xs tracking-wider transition-[background-color,color,transform] duration-200 active:scale-95 ${
                        r.approved
                          ? "border border-white/25 text-white hover:bg-white hover:text-black"
                          : "bg-volt text-black hover:bg-volt-dark"
                      }`}
                    >
                      {r.approved ? "Hide" : "Publish"}
                    </button>
                    <button
                      onClick={() => onDeleteReview(r.id)}
                      data-testid={`review-delete-${i + 1}`}
                      className="h-9 px-4 border border-white/25 text-white/60 font-anton uppercase text-xs tracking-wider transition-[background-color,color,border-color,transform] duration-200 hover:border-volt hover:text-volt active:scale-95"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-6 font-plex text-[11px] uppercase tracking-[0.2em] text-white/30">
              No reviews yet — they arrive after orders are marked posted + 5 days
            </p>
          )}
        </div>
        </div>

        <div data-testid="admin-panel-messages" className={tab === "inbox" ? "" : "hidden"}>
        <div data-testid="messages-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <MessageSquare size={22} className="text-volt" /> Contact inbox
          </p>
          <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-xl">
            Messages from the contact form land here and in your email. Hit Reply to answer them
            directly from your mail app.
          </p>

          {messages.length > 0 ? (
            <ul data-testid="message-list" className="mt-6 border-t border-white/10">
              {messages.map((m, i) => (
                <li
                  key={m.id}
                  data-testid={`message-row-${i + 1}`}
                  className="border-b border-white/10 py-4 flex flex-wrap items-start gap-x-6 gap-y-2"
                >
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-white/90 font-light text-sm leading-relaxed whitespace-pre-wrap">
                      {m.message}
                    </p>
                    <p className="mt-2 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                      {m.name} · {m.email} · {new Date(m.created_at).toLocaleDateString("en-AU")}{" "}
                      {new Date(m.created_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-auto">
                    <a
                      href={`mailto:${m.email}?subject=Re: Your message to Breethe`}
                      data-testid={`message-reply-${i + 1}`}
                      className="h-9 px-4 bg-volt text-black font-anton uppercase text-xs tracking-wider inline-flex items-center transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
                    >
                      Reply
                    </a>
                    <button
                      onClick={() => onDeleteMessage(m.id)}
                      data-testid={`message-delete-${i + 1}`}
                      className="h-9 px-4 border border-white/25 text-white/60 font-anton uppercase text-xs tracking-wider transition-[background-color,color,border-color,transform] duration-200 hover:border-volt hover:text-volt active:scale-95"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-6 font-plex text-[11px] uppercase tracking-[0.2em] text-white/30">
              No messages yet — the contact form is linked in the footer
            </p>
          )}
        </div>
        </div>

        <div data-testid="admin-panel-social" className={tab === "marketing" ? "" : "hidden"}>
        <SocialStudio />
        </div>

        <div data-testid="admin-panel-founders" className={tab === "marketing" ? "" : "hidden"}>
        <div className="mt-10 border border-white/10 overflow-x-auto">
          <table data-testid="founders-table" className="w-full min-w-[900px] text-left">
            <thead>
              <tr className="border-b border-white/10 bg-[#121212]">
                {["Email", "Would Pay", "Frequency", "Primary Use", "Used Before", "Suggestions", "Bought", "Joined"].map((h) => (
                  <th key={h} className="px-5 py-4 font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr
                  key={e.id}
                  data-testid={`founders-table-row-${i + 1}`}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                >
                  <td className="px-5 py-4 text-sm text-white">{e.email}</td>
                  <td className="px-5 py-4 text-sm text-volt font-plex">{e.price_point}</td>
                  <td className="px-5 py-4 text-sm text-white/70">{e.usage_frequency}</td>
                  <td className="px-5 py-4 text-sm text-white/70">{e.primary_use}</td>
                  <td className="px-5 py-4 text-sm text-white/70">{e.used_before}</td>
                  <td className="px-5 py-4 text-sm text-white/70 max-w-[220px]">
                    {e.suggestions ? (
                      <span className="font-light text-white/80">{e.suggestions}</span>
                    ) : (
                      <span className="text-white/25">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {boughtEmails.has(e.email) ? (
                      <span className="font-plex text-xs uppercase tracking-[0.15em] text-volt">Bought</span>
                    ) : (
                      <span className="text-white/25">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-white/40 font-plex text-xs">
                    {new Date(e.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center font-plex text-xs uppercase tracking-[0.25em] text-white/40">
                    No founders yet — share the link
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </div>

        <div data-testid="admin-panel-orders" className={tab === "orders" ? "" : "hidden"}>
        <div className="mt-12 flex items-baseline justify-between">
          <h2 className="font-anton uppercase text-2xl md:text-4xl">
            Orders<span className="text-volt">.</span>
          </h2>
          <span className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">
            {orderStats.total} {orderStats.total === 1 ? "order" : "orders"} — A${orderStats.revenue.toFixed(2)}
          </span>
        </div>
        <div className="mt-6 border border-white/10 overflow-x-auto">
          <table data-testid="orders-table" className="w-full min-w-[760px] text-left">
            <thead>
              <tr className="border-b border-white/10 bg-[#121212]">
                {["Email", "Ship To", "Plan", "Total", "Status", "Updates", "Fulfilment", "Date"].map((h) => (
                  <th key={h} className="px-5 py-4 font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr
                  key={o.session_id}
                  data-testid={`orders-table-row-${i + 1}`}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                >
                  <td className="px-5 py-4 text-sm text-white">{o.email}</td>
                  <td className="px-5 py-4 text-sm" data-testid={`order-shipto-${i + 1}`}>
                    {o.shipping && o.shipping.line1 ? (
                      <span className="block leading-relaxed">
                        <span className="text-white font-medium">{o.shipping.name}</span>
                        <span className="block text-white/60 text-xs font-light mt-0.5">
                          {o.shipping.line1}
                          {o.shipping.line2 ? `, ${o.shipping.line2}` : ""}
                          <br />
                          {o.shipping.city} {o.shipping.state} {o.shipping.postal_code}
                        </span>
                      </span>
                    ) : (
                      <span className="text-white/25 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-white/70">{o.plan}</td>
                  <td className="px-5 py-4 text-sm text-volt font-plex">
                    {o.currency}${o.amount.toFixed(2)}
                  </td>
                  <td className="px-5 py-4 font-plex text-xs uppercase tracking-[0.15em] text-white/70">
                    {o.payment_status}
                  </td>
                  <td className="px-5 py-4" data-testid={`order-emails-${i + 1}`}>
                    <span className="flex flex-wrap gap-1.5">
                      {[
                        ["Receipt", o.emails?.receipt],
                        ...(o.lookup_key === "n001_trial" ? [["Survey", o.emails?.survey]] : []),
                        ["Posted", o.emails?.shipped],
                        ["Review", o.emails?.review || (o.review_request_sent ? true : null)],
                      ].map(([label, sent]) => (
                        <span
                          key={label}
                          title={
                            sent
                              ? `Sent${typeof sent === "string" ? ` ${new Date(sent).toLocaleDateString("en-AU")}` : ""}`
                              : "Not sent yet"
                          }
                          className={`px-2 py-1 font-plex text-[9px] uppercase tracking-[0.15em] border ${
                            sent ? "border-volt/60 text-volt" : "border-white/10 text-white/25"
                          }`}
                        >
                          {label}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {o.fulfilment === "posted" && !editingShip[o.session_id] ? (
                      <span className="flex flex-col gap-1.5">
                        <span className="flex items-center gap-3">
                          <span className="font-plex text-xs uppercase tracking-[0.15em] text-volt">
                            Posted · {o.tracking_number}
                          </span>
                          <button
                            onClick={() =>
                              setEditingShip((m) => ({ ...m, [o.session_id]: true }))
                            }
                            data-testid={`ship-edit-button-${i + 1}`}
                            className="font-plex text-[10px] uppercase tracking-[0.15em] text-white/40 hover:text-volt transition-colors"
                          >
                            Edit
                          </button>
                        </span>
                        {o.tracking_status ? (
                          <span
                            data-testid={`tracking-status-${i + 1}`}
                            className={`font-plex text-[10px] uppercase tracking-[0.15em] ${
                              o.tracking_status.status.toLowerCase().includes("deliver")
                                ? "text-volt"
                                : "text-white/50"
                            }`}
                          >
                            ● {o.tracking_status.status}
                            {o.tracking_status.location ? ` — ${o.tracking_status.location}` : ""}
                            {o.tracking_status.last_event ? ` · ${o.tracking_status.last_event}` : ""}
                          </span>
                        ) : (
                          <span className="font-plex text-[10px] uppercase tracking-[0.15em] text-white/30">
                            {trackingConfigured
                              ? "Awaiting first scan"
                              : "Live status off — AusPost key needed"}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <input
                          value={trackingInputs[o.session_id] || ""}
                          onChange={(e) =>
                            setTrackingInputs((m) => ({ ...m, [o.session_id]: e.target.value }))
                          }
                          placeholder="AUSPOST TRACKING #"
                          data-testid={`ship-tracking-input-${i + 1}`}
                          className="h-9 w-44 bg-transparent border border-white/20 focus:border-volt px-3 font-plex text-xs uppercase tracking-[0.1em] text-white placeholder:text-white/25 outline-none transition-colors"
                        />
                        <button
                          onClick={() => onShip(o.session_id)}
                          disabled={shipping === o.session_id}
                          data-testid={`ship-order-button-${i + 1}`}
                          className="h-9 px-4 bg-volt text-black font-anton uppercase text-xs tracking-wider transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60"
                        >
                          {shipping === o.session_id
                            ? "Sending…"
                            : o.fulfilment === "posted"
                              ? "Update & Resend"
                              : "Mark Posted"}
                        </button>
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-white/40 font-plex text-xs">
                    {new Date(o.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center font-plex text-xs uppercase tracking-[0.25em] text-white/40">
                    No orders yet — flip the drop switch
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </div>
      </main>
      </div>

      <nav
        data-testid="admin-bottomnav"
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#121212] border-t border-white/10 grid grid-cols-7"
      >
        {TABS.map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => goTab(id)}
            data-testid={`admin-tab-mobile-${id}`}
            aria-label={label}
            className={`h-16 flex flex-col items-center justify-center gap-1 transition-colors duration-150 active:scale-95 ${
              tab === id ? "text-volt" : "text-white/40"
            }`}
          >
            <Icon size={18} />
            <span className="font-plex text-[8px] uppercase tracking-[0.15em]">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default Admin;
